/**
 * API client for Community Futures Chatbot backend.
 *
 * Provides type-safe methods for all backend endpoints with Zod validation.
 */

import axios, { type AxiosInstance, type AxiosError } from 'axios'
import type {
  Session,
  ConversationTurn,
  GeneratedImage,
} from '@/models'
import type { AgeRange } from '@/components/session/SessionConfig'

/**
 * API client configuration
 */
export interface ApiClientConfig {
  baseURL: string
  timeout?: number
}

/**
 * Image generation grounding mode.
 * Controls how firmly images are tied to a specific location.
 */
export type ImageMode = 'grounded' | 'nearly_grounded' | 'free_form' | 'abstract'

/**
 * API error response
 */
export interface ApiError {
  error: string
  message: string
  detail?: string | Array<{ loc?: string[]; msg?: string; type?: string }>
  status_code: number
  request_id?: string
  details?: Record<string, unknown>
}

/**
 * Create session request
 */
export interface CreateSessionRequest {
  session_type: 'individual' | 'group' | 'ambient'
  participant_count: number
  facilitator_id: string
  facilitator_name?: string
  location: string
  consent_text_shown: string
  consent_given: boolean
  participant_id: string
  /** Image generation grounding mode (defaults to nearly_grounded on backend) */
  image_mode?: ImageMode
  /** UK postcode for geographic/demographic analysis */
  postcode?: string
  /** ONS standard age ranges of participants */
  age_ranges?: AgeRange[]
  /** Event ID — when present, backend inherits grounding_image_ids and image_mode from event config */
  event_id?: string
  /** Grounding image IDs inherited from event config at join time */
  grounding_image_ids?: string[]
}

/**
 * Create session response
 */
export interface CreateSessionResponse {
  session_id: string
  status: string
  image_url?: string
  created_at: string
}

/**
 * Send message request
 */
export interface SendMessageRequest {
  content: string
  input_mode: 'text' | 'voice' | 'structured' | 'image'
  participant_id: string
  image_data?: string // Base64 encoded image
}

/**
 * Input type for structured questions
 */
export type InputType = 'text' | 'timeframe_slider' | 'image_confirmation'

/**
 * Options for structured inputs
 */
export interface InputOptions {
  values?: Array<{ value: string; label: string }>
  default_index?: number
  // Image confirmation options
  prompt?: string
  confirm_text?: string
  modify_text?: string
}

/**
 * Image option for selection
 */
export interface ImageOption {
  image_id: string
  image_url: string
}

/**
 * Image variation within a generation round
 */
export interface ImageVariation {
  image_id: string
  variant_index: number
  image_url: string
}

/**
 * Generate images request
 */
export interface GenerateImagesRequest {
  feedback: string
  selected_image_id?: string
  /** Facilitator-edited vision description to use instead of auto-generated prompt */
  prompt_override?: string
  /** Grounding image ID to use as starting point (MODE 2 image-to-image) */
  grounding_image_id?: string
}

/**
 * Ambient audio chunk response
 */
export interface AmbientChunkResponse {
  chunk_index: number
  text: string
  cumulative_word_count: number
}

/**
 * Ambient recording stop response
 */
export interface AmbientStopResponse {
  total_chunks: number
  total_words: number
}

/**
 * Ambient theme extraction
 */
export interface AmbientTheme {
  topic: string
  points: string[]
}

/**
 * Ambient extraction response
 */
export interface AmbientExtractionResponse {
  themes: AmbientTheme[]
  summary: string
  key_quotes: string[]
  participant_count_estimate: number
  confidence: number
  total_words: number
  /**
   * False when extraction succeeded but the themes/summary failed to write to
   * Supabase. UI surfaces a warning so the user can copy themes before leaving.
   *
   * Optional for back-compat with older backends that omit the field — frontend
   * uses a strict `=== false` check so a missing field is treated as "assume
   * persisted" (fail-open). Newer backends always populate it.
   */
  persisted?: boolean
}

/**
 * Vision description response
 */
export interface VisionDescriptionResponse {
  session_id: string
  description: string
  character_count: number
}

/**
 * Generate images response (4 variations per round)
 */
export interface GenerateImagesResponse {
  round_number: number
  image_options: ImageVariation[]
  generation_duration_ms: number
  prompt_used: string
  can_refine: boolean
  grounding_image_attribution?: string | null
  grounding_image_source?: string | null
  grounding_applied?: boolean | null
  generation_mode?: string | null
}

/**
 * Grounding image option for the picker
 */
export interface GroundingImageOption {
  id: string
  title?: string | null
  thumbnail_url: string
  image_url: string
  source: 'admin' | 'geograph' | 'mapillary'
  attribution?: string | null
  licence?: string | null
}

/**
 * Grounding image set response
 */
export interface GroundingImageSetResponse {
  tier: 1 | 2 | 3
  images: GroundingImageOption[]
  location: string
  error?: string | null
}

/**
 * Influence description response
 */
export interface InfluenceDescriptionResponse {
  description: string
}

/**
 * Early conversation completion response (facilitator shortcut)
 */
export interface EarlyCompleteResponse {
  conversation_summary: string
  vision_description?: string
  topics_covered: string[]
  user_turns: number
}

/**
 * Response from recording the participant's final image selection
 */
export interface ImageSelectionResponse {
  session_id: string
  selected_image_id: string
  remaining_refinements: number
}

/**
 * Send message response
 */
/**
 * Topic coverage from backend
 */
export interface TopicCoverageResponse {
  covered_topics: string[]
  required_covered: number
  required_total: number
  all_required_met: boolean
}

export interface SendMessageResponse {
  turn_id: string
  system_response: string
  dialogue_complete: boolean
  image_url?: string  // For backwards compatibility
  image_options?: ImageOption[] | null  // 4 images for selection
  input_type?: InputType
  input_options?: InputOptions | null
  topic_coverage?: TopicCoverageResponse
  // Image generation confirmation flow
  awaiting_image_confirmation?: boolean
  conversation_summary?: string
  vision_description?: string
  // Graceful degradation when image generation not available
  image_generation_unavailable?: boolean
  // Suggested next topic to explore
  next_topic_suggestion?: string | null
  // Explicit confirmation before ending conversation (issue #173)
  awaiting_final_confirmation?: boolean
}

/**
 * Request image iteration
 */
export interface RequestImageIterationRequest {
  refinement_feedback: string
}

/**
 * API Client for backend communication
 */
export class ApiClient {
  private client: AxiosInstance

  constructor(config: ApiClientConfig) {
    this.client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 240000, // 4 minutes for image generation
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Response interceptor for error handling.
    // Preserves response.status on the thrown error so callers (e.g. retry
    // logic in transcribeAudio) can still inspect the HTTP status code.
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiError>) => {
        const rawDetail = error.response?.data?.detail
        const message = Array.isArray(rawDetail)
          ? rawDetail.map((e) => `${(e.loc ?? []).join('.')} — ${e.msg}`).join('; ')
          : rawDetail || error.response?.data?.message || error.message || 'API request failed'
        const enrichedError = new Error(message) as Error & { response?: { status: number } }
        if (error.response?.status) {
          enrichedError.response = { status: error.response.status }
        }
        throw enrichedError
      }
    )
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; version: string; environment: string }> {
    const response = await this.client.get<{ status: string; version: string; environment: string }>('/health')
    return response.data
  }

  /**
   * Create new session (initiates workflow)
   */
  async createSession(request: CreateSessionRequest): Promise<CreateSessionResponse> {
    const response = await this.client.post<CreateSessionResponse>(
      '/api/v1/sessions',
      request
    )
    return response.data
  }

  /**
   * Get session by ID
   */
  async getSession(sessionId: string): Promise<Session> {
    const response = await this.client.get<Session>(`/api/v1/sessions/${sessionId}`)
    return response.data
  }

  /**
   * Send conversation message
   */
  async sendMessage(
    sessionId: string,
    request: SendMessageRequest
  ): Promise<SendMessageResponse> {
    const response = await this.client.post<SendMessageResponse>(
      `/api/v1/sessions/${sessionId}/conversation`,
      request
    )
    return response.data
  }

  /**
   * Get conversation transcript
   */
  async getTranscript(sessionId: string): Promise<ConversationTurn[]> {
    const response = await this.client.get<ConversationTurn[]>(
      `/api/v1/sessions/${sessionId}/transcript`
    )
    return response.data
  }

  /**
   * Get generated images for session
   */
  async getImages(sessionId: string): Promise<GeneratedImage[]> {
    const response = await this.client.get<GeneratedImage[]>(
      `/api/v1/sessions/${sessionId}/images`
    )
    return response.data
  }

  /**
   * Get specific image
   */
  async getImage(sessionId: string, imageId: string): Promise<GeneratedImage> {
    const response = await this.client.get<GeneratedImage>(
      `/api/v1/sessions/${sessionId}/images/${imageId}`
    )
    return response.data
  }

  /**
   * Record the participant's chosen image as the final selection.
   * The image may be an initial option or a refined (re-iterated) image.
   */
  async selectImage(
    sessionId: string,
    imageId: string
  ): Promise<ImageSelectionResponse> {
    const response = await this.client.post<ImageSelectionResponse>(
      `/api/v1/sessions/${sessionId}/images/${imageId}/select`
    )
    return response.data
  }

  async saveParticipantEmail(sessionId: string, email: string): Promise<void> {
    await this.client.post(`/api/v1/sessions/${sessionId}/email`, { email })
  }

  /**
   * Request image iteration (refinement)
   * @deprecated Use generateImages() for new multi-variant flow
   */
  async requestImageIteration(
    sessionId: string,
    request: RequestImageIterationRequest
  ): Promise<GeneratedImage> {
    const response = await this.client.post<GeneratedImage>(
      `/api/v1/sessions/${sessionId}/images`,
      request
    )
    return response.data
  }

  /**
   * Generate 4 image variations
   *
   * Round 1: Initial generation from conversation themes (or with user upload)
   * Round 2-5: Refinement using selected image from previous round
   */
  async generateImages(
    sessionId: string,
    request: GenerateImagesRequest
  ): Promise<GenerateImagesResponse> {
    const formData = new FormData()
    formData.append('feedback', request.feedback)

    if (request.selected_image_id) {
      formData.append('selected_image_id', request.selected_image_id)
    }

    if (request.prompt_override) {
      formData.append('prompt_override', request.prompt_override)
    }

    if (request.grounding_image_id) {
      formData.append('grounding_image_id', request.grounding_image_id)
    }

    const response = await this.client.post<GenerateImagesResponse>(
      `/api/v1/sessions/${sessionId}/images/generate`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  /**
   * Complete conversation early (facilitator shortcut).
   * Generates summary from whatever content has been gathered so far.
   * Requires at least 2 conversation turns.
   */
  async completeEarly(
    sessionId: string
  ): Promise<EarlyCompleteResponse> {
    const response = await this.client.post<EarlyCompleteResponse>(
      `/api/v1/sessions/${sessionId}/early-complete`
    )
    return response.data
  }

  /**
   * Get auto-generated vision description for editing
   */
  async getVisionDescription(
    sessionId: string
  ): Promise<VisionDescriptionResponse> {
    const response = await this.client.get<VisionDescriptionResponse>(
      `/api/v1/sessions/${sessionId}/vision-description`
    )
    return response.data
  }

  /**
   * Get grounding images for a session (tiered cascade)
   */
  async getGroundingImages(
    sessionId: string,
    signal?: AbortSignal
  ): Promise<GroundingImageSetResponse> {
    const response = await this.client.get<GroundingImageSetResponse>(
      `/api/v1/sessions/${sessionId}/grounding-images`,
      { signal }
    )
    return response.data
  }

  /**
   * Preview grounding images without a session (landing screen pebbles)
   */
  async getGroundingImagesPreview(
    location?: string,
    plannedSessionId?: string,
    signal?: AbortSignal
  ): Promise<GroundingImageSetResponse> {
    const params = new URLSearchParams()
    if (location) params.set('location', location)
    if (plannedSessionId) params.set('planned_session_id', plannedSessionId)
    const qs = params.toString()
    const response = await this.client.get<GroundingImageSetResponse>(
      `/api/v1/grounding-images/preview${qs ? `?${qs}` : ''}`,
      { signal }
    )
    return response.data
  }

  /**
   * Get influence description for how conversation shaped the image
   */
  async getInfluenceDescription(
    sessionId: string,
    signal?: AbortSignal
  ): Promise<InfluenceDescriptionResponse> {
    const response = await this.client.post<InfluenceDescriptionResponse>(
      `/api/v1/sessions/${sessionId}/influence-description`,
      undefined,
      { signal }
    )
    return response.data
  }

  /**
   * Transcribe audio (voice input).
   *
   * Uses 120s timeout (backend can retry up to ~90s) and retries once on
   * transient server errors (502, 503) for reliability. FormData is
   * recreated per attempt — some browsers treat it as a consumed stream.
   */
  /**
   * Upload an ambient audio chunk for transcription
   */
  async sendAmbientAudioChunk(
    sessionId: string,
    audioData: Blob,
    chunkIndex: number
  ): Promise<AmbientChunkResponse> {
    const formData = new FormData()
    formData.append('audio', audioData)
    formData.append('chunk_index', String(chunkIndex))

    const response = await this.client.post<AmbientChunkResponse>(
      `/api/v1/sessions/${sessionId}/ambient-audio`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      }
    )
    return response.data
  }

  /**
   * Stop ambient recording
   */
  async stopAmbientRecording(
    sessionId: string
  ): Promise<AmbientStopResponse> {
    const response = await this.client.post<AmbientStopResponse>(
      `/api/v1/sessions/${sessionId}/ambient-audio/stop`
    )
    return response.data
  }

  /**
   * Extract themes from ambient transcript
   */
  async extractAmbientThemes(
    sessionId: string
  ): Promise<AmbientExtractionResponse> {
    const response = await this.client.post<AmbientExtractionResponse>(
      `/api/v1/sessions/${sessionId}/ambient-audio/extract`,
      undefined,
      { timeout: 180000 } // Extraction can take time for long transcripts
    )
    return response.data
  }

  async transcribeAudio(
    sessionId: string,
    audioData: Blob
  ): Promise<{ text: string; confidence: number }> {
    const maxAttempts = 2
    let lastError: Error | null = null

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Create fresh FormData per attempt — consumed after first send in some browsers
      const formData = new FormData()
      formData.append('audio', audioData)

      try {
        const response = await this.client.post<{ text: string; confidence: number }>(
          `/api/v1/sessions/${sessionId}/voice`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            timeout: 120000, // 120s — backend may retry up to ~90s total
          }
        )
        return response.data
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err))

        // Only retry on transient server errors, not client errors (4xx)
        const status = (err as { response?: { status?: number } })?.response?.status
        const isRetryable = status !== undefined && (status === 502 || status === 503)
        if (isRetryable && attempt < maxAttempts - 1) {
          // Brief delay before retry
          await new Promise(resolve => setTimeout(resolve, 1000))
          continue
        }
        break
      }
    }

    throw lastError ?? new Error('Transcription failed')
  }
}

/**
 * Create API client instance
 */
export function createApiClient(config?: Partial<ApiClientConfig>): ApiClient {
  const baseURL = config?.baseURL ?? (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? 'http://localhost:8000'

  return new ApiClient({
    baseURL,
    timeout: config?.timeout,
  })
}
